import { Input, Select, Textarea } from '@/components/ui/FormControls';
import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { salesRpc } from '@/lib/salesOverview';
import type { SaleShareSettings } from '@klothiq/domain';
import ErrorState from '@/components/common/ErrorState';
import LoadingState from '@/components/common/LoadingState';
import { toast } from 'sonner';

type Settings = SaleShareSettings & { version: number };
export default function SalesMessagingSettings() {
  const { organization, can } = useAuth(),
    cache = useQueryClient();
  const [draft, setDraft] = useState<Settings>();
  const [dirty, setDirty] = useState(false);
  const query = useQuery({
    queryKey: ['sale_share_settings', organization?.id],
    queryFn: () => salesRpc<Settings>('get_sale_share_settings', {}),
    enabled: can('master_config.view'),
  });
  useEffect(() => {
    if (query.data && !dirty) setDraft(query.data);
  }, [query.data, dirty]);
  const save = useMutation({
    mutationFn: () => {
      if (!draft) throw new Error('Load settings first');
      const { version, ...settings } = draft;
      return salesRpc<Settings>('save_sale_share_settings', {
        p_data: { ...settings, expected_version: version },
      });
    },
    onSuccess: (result) => {
      cache.setQueryData(['sale_share_settings', organization?.id], result);
      setDraft(result);
      setDirty(false);
      void cache.invalidateQueries({ queryKey: ['sale_whatsapp'] });
      toast.success('Sales messaging settings saved');
    },
    onError: (e) => toast.error(e.message),
  });
  function update<K extends keyof Settings>(key: K, value: Settings[K]) {
    setDraft((d) => (d ? { ...d, [key]: value } : d));
    setDirty(true);
  }
  if (query.isLoading) return <LoadingState />;
  if (query.error)
    return <ErrorState message={query.error.message} onRetry={() => void query.refetch()} />;
  if (!draft) return null;
  const field =
    'mt-1 min-h-11 w-full rounded-lg border border-border bg-background px-3 text-sm text-foreground';
  const editable = can('master_config.manage') && !save.isPending;
  return (
    <form
      className="max-w-2xl space-y-5 rounded-xl border border-border bg-card p-4 sm:p-6"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <div>
        <h2 className="font-semibold">Sales messaging</h2>
        <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
          Staff preview saved sale details and open the customer chat. Messages are sent manually in
          WhatsApp. Access is controlled by the Share sale privilege.
        </p>
      </div>
      <fieldset disabled={!editable} className="space-y-4 disabled:opacity-70">
        <label className="flex min-h-11 items-center gap-3 text-sm">
          <Input
            type="checkbox"
            checked={draft.enabled}
            onChange={(e) => update('enabled', e.target.checked)}
            helpLabel="Enable WhatsApp sharing"
          />
          Enable WhatsApp sharing
        </label>
        <label className="block text-sm">
          Business name
          <Input
            required
            minLength={2}
            maxLength={120}
            value={draft.business_name}
            onChange={(e) => update('business_name', e.target.value)}
            className={field}
            helpLabel="Business name"
          />
        </label>
        <label className="block text-sm">
          Default phone format
          <Select
            value={draft.default_country}
            onChange={(e) => update('default_country', e.target.value as Settings['default_country'])}
            className={field}
            helpLabel="Default phone format"
          >
            <option value="IN">India (+91)</option>
            <option value="international">International (+country code)</option>
          </Select>
        </label>
        <label className="block text-sm">
          Closing message
          <Textarea
            maxLength={500}
            rows={3}
            value={draft.closing_text}
            onChange={(e) => update('closing_text', e.target.value)}
            className={`${field} py-3`}
            helpLabel="Closing message"
          />
        </label>
        <label className="flex min-h-11 items-center gap-3 text-sm">
          <Input
            type="checkbox"
            checked={draft.include_address}
            onChange={(e) => update('include_address', e.target.checked)}
            helpLabel="Include delivery address by default"
          />
          Include delivery address by default
        </label>
        <label className="flex min-h-11 items-center gap-3 text-sm">
          <Input
            type="checkbox"
            checked={draft.include_seller}
            onChange={(e) => update('include_seller', e.target.checked)}
            helpLabel="Include seller name"
          />
          Include seller name
        </label>
      </fieldset>
      {save.error && (
        <p role="alert" className="text-sm text-destructive">
          {save.error.message}
        </p>
      )}
      <div className="flex flex-wrap justify-end gap-2">
        <button
          type="button"
          onClick={() => {
            setDirty(false);
            void query.refetch();
          }}
          disabled={save.isPending}
          className="min-h-11 rounded-lg border border-border px-4 text-sm"
        >
          Reload settings
        </button>
        {can('master_config.manage') && (
          <button
            disabled={!dirty || save.isPending}
            className="min-h-11 rounded-lg bg-primary px-4 text-sm font-semibold text-primary-foreground disabled:opacity-50"
          >
            {save.isPending ? 'Saving…' : 'Save settings'}
          </button>
        )}
      </div>
    </form>
  );
}
